import { useEffect, useRef, useState } from 'react';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { resolveAnchorElement } from '../driver/anchors';
import { useAiAssistant } from '../context/AiAssistantContext';
import { isInViewport } from './visibility';

const useStyles = makeStyles(theme => ({
  paper: {
    maxWidth: 320,
    padding: theme.spacing(2),
    // Above MUI dialogs (1300) so the coach-mark is never occluded.
    zIndex: 1500,
    boxShadow: theme.shadows[8],
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  // A triangle that points from the coach-mark up at the highlighted element. popper.js positions it
  // horizontally along the top edge, aligned to the anchor, via the `arrow` modifier.
  arrow: {
    position: 'absolute',
    top: 0,
    width: 0,
    height: 0,
    marginTop: '-8px',
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderBottom: `8px solid ${theme.palette.primary.main}`,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  recap: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  terminalNote: {
    marginTop: theme.spacing(1),
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
}));

const HIGHLIGHT_OUTLINE = '3px solid #1976d2';

/** A virtual anchor at viewport center, used for navigate steps that have no DOM target. */
function centerAnchor(): { getBoundingClientRect: () => DOMRect } {
  return {
    getBoundingClientRect: () =>
      ({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
        right: window.innerWidth / 2,
        bottom: window.innerHeight / 2,
        width: 0,
        height: 0,
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
        toJSON: () => {},
      } as DOMRect),
  };
}

/**
 * The coach-mark overlay. Renders a Popper pointing at the driver's current data-ai-anchor, shows the
 * step's narration + a step recap, and offers Next / Done. It never advances or submits on its own —
 * the terminal step just highlights Save and tells the user to click it.
 */
export const Coachmark = () => {
  const classes = useStyles();
  const {
    driverStatus,
    currentStep,
    currentAnchor,
    activeStepIndex,
    totalSteps,
    driverError,
    next,
    cancelPlan,
  } = useAiAssistant();

  const [anchorEl, setAnchorEl] = useState<
    HTMLElement | { getBoundingClientRect: () => DOMRect } | null
  >(null);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [targetVisible, setTargetVisible] = useState(true);
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);

  // Track whether the highlighted element is on-screen, so "Show me" only appears once it scrolls out
  // of view. Capture-phase scroll catches inner scroll containers (config-editor panes), not just window.
  useEffect(() => {
    if (!targetEl) {
      setTargetVisible(true);
      return undefined;
    }
    // Coalesce bursts of scroll events into one check per frame — the raw capture-phase scroll can fire
    // hundreds of times a second across nested scrollers.
    let raf = 0;
    const update = () => setTargetVisible(isInViewport(targetEl));
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        update();
      });
    };
    update();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [targetEl]);

  // Resolve + highlight the anchored element whenever the target changes.
  useEffect(() => {
    if (driverStatus === 'idle') {
      setAnchorEl(null);
      setTargetEl(null);
      return undefined;
    }
    const el = resolveAnchorElement(currentAnchor);
    setAnchorEl(el ?? centerAnchor());
    setTargetEl(el ?? null);
    if (!el) return undefined;

    const prevOutline = el.style.outline;
    const prevOffset = el.style.outlineOffset;
    el.style.outline = HIGHLIGHT_OUTLINE;
    el.style.outlineOffset = '2px';
    return () => {
      el.style.outline = prevOutline;
      el.style.outlineOffset = prevOffset;
    };
  }, [currentAnchor, driverStatus]);

  // Move keyboard focus to the Next button when the drive pauses for the user, so a keyboard-only user
  // isn't left hunting for the control after each step.
  const nextBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (driverStatus === 'awaiting-user') nextBtnRef.current?.focus();
  }, [driverStatus, activeStepIndex]);

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const active =
    driverStatus === 'running' ||
    driverStatus === 'awaiting-user' ||
    driverStatus === 'done' ||
    driverStatus === 'error';

  if (!active || !currentStep) return null;

  const isTerminal = !!currentStep.terminal;
  const stepLabel = totalSteps > 0 ? `Step ${activeStepIndex + 1} of ${totalSteps}` : '';

  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl as any}
      placement="bottom"
      modifiers={{
        offset: { enabled: true, offset: '0,10' },
        // Keep the coach-mark on screen; keep it BELOW the target so the up-arrow always points up.
        preventOverflow: { enabled: true, boundariesElement: 'viewport' },
        flip: { enabled: false },
        // Only enable the arrow once its element has mounted (arrowRef is null on first render).
        arrow: { enabled: Boolean(arrowRef), element: arrowRef },
      }}
      style={{ zIndex: 1500 }}
    >
      <>
        <span ref={setArrowRef} className={classes.arrow} />
        <Paper
          className={classes.paper}
          data-testid="ai-coachmark"
          elevation={8}
          role="dialog"
          aria-label="Config Copilot guidance"
        >
          <Typography variant="caption" className={classes.brand}>
            ✨ Config Copilot
          </Typography>
          {stepLabel && (
            <Typography variant="caption" className={classes.recap}>
              {stepLabel}
            </Typography>
          )}
          {/* role=status + aria-live so each new step's narration is spoken to screen readers. */}
          <div role="status" aria-live="polite">
            {driverStatus === 'error' ? (
              <Typography variant="body2" color="error">
                {driverError ?? 'The assistant hit a problem driving the UI.'}
              </Typography>
            ) : (
              <Typography variant="body2">{currentStep.tooltip}</Typography>
            )}
            {driverStatus === 'running' && (
              <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="textSecondary">
                  Working…
                </Typography>
              </Box>
            )}
          </div>

          {isTerminal && driverStatus === 'done' && (
            <Typography variant="body2" className={classes.terminalNote}>
              {currentStep.anchor?.startsWith('scaffolder')
                ? 'Review the inputs, then click Create yourself to run the scaffolder.'
                : 'Review the change, then click Save yourself to open the PR.'}
            </Typography>
          )}

          <Box className={classes.actions}>
            {targetEl && !targetVisible && (
              <Button
                size="small"
                style={{ marginRight: 'auto' }}
                onClick={() =>
                  targetEl.scrollIntoView({
                    behavior: prefersReducedMotion ? 'auto' : 'smooth',
                    block: 'center',
                  })
                }
                data-testid="ai-coachmark-recenter"
                title="Scroll the highlighted element into view"
              >
                Show me
              </Button>
            )}
            <Button size="small" onClick={cancelPlan} data-testid="ai-coachmark-close">
              {driverStatus === 'done' || driverStatus === 'error' ? 'Done' : 'Cancel'}
            </Button>
            {driverStatus === 'awaiting-user' && (
              <Button
                ref={nextBtnRef}
                size="small"
                variant="contained"
                color="primary"
                onClick={next}
                data-testid="ai-coachmark-next"
              >
                Next
              </Button>
            )}
          </Box>
        </Paper>
      </>
    </Popper>
  );
};
